import * as amplitude from "@amplitude/analytics-browser";
import { Button, Flex, Skeleton, Text } from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { RiSurveyLine } from "react-icons/ri";
import { Link } from "react-router-dom";
import { GenericError } from "src/components/Feedback/GenericError";
import { InfoAlert } from "src/components/Feedback/InfoAlert";
import { HorizontallyBoundedContainer } from "src/components/Layout/HorizontallyBoundedContainer";
import { ParentFormsLayoutFooter } from "src/components/Layout/ParentFormsLayoutFooter";
import { ParentFormsLayoutInner } from "src/components/Layout/ParentFormsLayoutInner";
import { ParentFormsLayoutOuter } from "src/components/Layout/ParentFormsLayoutOuter";
import { RemoteDataView } from "src/components/Layout/RemoteDataView";
import { Glossary } from "src/components/Text/Glossary";
import { useOrganization } from "src/hooks/useOrganization";
import { useRemoteDataQuery } from "src/hooks/useRemoteDataQuery";
import { useUserProfile } from "src/hooks/useUserProfile";
import {
  getContext,
  saveContext,
  School,
} from "src/scenes/parent/state/localStorage";
import {
  getContext as getSearchContext,
  saveContext as saveSearchContext,
} from "src/scenes/state/localStorage";
import * as Url from "src/services/url";
import * as GQL from "src/types/graphql";
import { PendingPayments } from "../payments/PendingPayments";
import { Empty } from "../students";
import { NoForms } from "./components/NoForms";
import { StudentFormTemplateCards } from "./components/StudentFormTemplateCards";
import {
  GET_PARENT_ASSIGNED_FORMS,
  GET_PARENT_FORMS_INDEX,
  SCHOOLS_BY_SCHOOL_REFERENCE_IDS,
} from "./graphql/queries";

export function List() {
  const organization = useOrganization();
  const userProfile = useUserProfile();

  const [numSavedSchools, setNumSavedSchools] = useState(
    getContext()?.exploreSavedSchools.length ?? 0
  );

  const exploreSavedSchoolRefIDs: string[] =
    getSearchContext()?.exploreSavedSchoolRefIDs ?? [];

  const {
    remoteData: assignedFormRemoteData,
    startPolling,
    stopPolling,
  } = useRemoteDataQuery<
    GQL.GetParentAssignedForms,
    GQL.GetParentAssignedFormsVariables
  >(GET_PARENT_ASSIGNED_FORMS, {
    variables: {
      organization_id: organization.map((org) => org.id).withDefault(""),
    },
    skip: !userProfile.hasData() || !organization.hasData(),
    pollInterval: 30000,
    fetchPolicy: "cache-and-network",
  });

  useEffect(() => {
    const interval = setInterval(() => {
      stopPolling();
    }, 600000);
    return () => clearInterval(interval);
  }, [startPolling, stopPolling]);

  const { remoteData: parentFormIndexRemoteData } = useRemoteDataQuery<
    GQL.GetParentFormsIndex,
    GQL.GetParentFormsIndexVariables
  >(GET_PARENT_FORMS_INDEX, {
    variables: {
      organization_id: organization.map((org) => org.id).withDefault(""),
      guardian_id: userProfile.map((user) => user.id).withDefault(""),
    },
    skip: !userProfile.hasData() || !organization.hasData(),
    fetchPolicy: "cache-and-network",
  });

  const { remoteData: exploreSavedSchoolIDs } = useRemoteDataQuery<
    GQL.SchoolsBySchoolReferenceIDs,
    GQL.SchoolsBySchoolReferenceIDsVariables
  >(SCHOOLS_BY_SCHOOL_REFERENCE_IDS, {
    variables: {
      school_reference_ids: exploreSavedSchoolRefIDs,
      organization_id: organization.map((org) => org.id).withDefault(""),
    },
    skip: !exploreSavedSchoolRefIDs.length || !organization.hasData(),
  });

  useEffect(() => {
    const refIDs = getSearchContext()?.exploreSavedSchoolRefIDs ?? [];
    if (exploreSavedSchoolIDs.hasData() && refIDs.length > 0) {
      saveSearchContext({ exploreSavedSchoolRefIDs: [] });
      const schools: School[] = exploreSavedSchoolIDs.data.school.map(
        (item) => ({
          id: item.id,
          referenceID: item.reference_id ?? "",
          name: item.name,
        })
      );

      saveContext({
        exploreSavedSchools: schools,
        unresolvedReferenceIDs: refIDs.filter(
          (item) => !schools.map((school) => school.referenceID).includes(item)
        ),
      });
      setNumSavedSchools(schools.length);
    }
  }, [exploreSavedSchoolIDs]);

  return (
    <ParentFormsLayoutOuter heading="Home">
      <RemoteDataView
        loading={<Loading />}
        reloading={null}
        remoteData={parentFormIndexRemoteData}
        error={() => <GenericError />}
      >
        {(parentFormIndex) => {
          const allStudents = parentFormIndex.person;
          const studentsWithForms = allStudents.filter(
            (student) => student.forms.length > 0
          );

          let content = null;
          let footers = null;

          if (allStudents.length === 0) {
            content = (
              <Empty
                returnUrl={organization.map(Url.Parent.index).withDefault("#")}
              />
            );
          } else if (studentsWithForms.length === 0) {
            content = <NoForms />;
          } else {
            // Home: every form across active org enrollment periods. Per-period narrowing is only on Start New Form.
            content = (
              <Flex direction="column" gap={8}>
                {studentsWithForms.map((student) => (
                  <StudentFormTemplateCards
                    forms={student.forms}
                    assignedForms={
                      assignedFormRemoteData.toNullable()
                        ?.assigned_form_next_form ?? []
                    }
                    key={student.id}
                    student={student}
                  />
                ))}
              </Flex>
            );

            footers = (
              <ParentFormsLayoutFooter justifyContent="stretch">
                <Button
                  as={Link}
                  flexGrow={1}
                  leftIcon={<RiSurveyLine />}
                  to={organization.map(Url.Parent.Form.new).withDefault("#")}
                  onClick={() => {
                    amplitude.track("Start form clicked", {
                      action_source: "main_page",
                    });
                  }}
                >
                  <Glossary>Start form</Glossary>
                </Button>
              </ParentFormsLayoutFooter>
            );
          }

          return (
            <ParentFormsLayoutInner footers={footers}>
              {numSavedSchools > 0 && (
                <InfoAlert
                  children={
                    <Text>
                      <Glossary>
                        Your saved schools are ready to add to a new form.
                      </Glossary>
                    </Text>
                  }
                ></InfoAlert>
              )}
              <PendingPayments />
              {content}
            </ParentFormsLayoutInner>
          );
        }}
      </RemoteDataView>
    </ParentFormsLayoutOuter>
  );
}

const Loading = () => {
  return (
    <HorizontallyBoundedContainer>
      <Flex
        direction="column"
        gap="10"
        alignItems="left"
        marginY="10"
        marginX="4"
      >
        <Skeleton width="200px" height="2rem" />
        <Skeleton width="100%" height="20rem" />
        <Skeleton width="100%" height="20rem" />
      </Flex>
    </HorizontallyBoundedContainer>
  );
};
